import api from './api';
import {
  createOptionsDemoAlertRule,
  deleteOptionsDemoAlertRule,
  getOptionsDemoAccount,
  getOptionsDemoAlerts,
  getOptionsDemoAnalytics,
  markOptionsDemoAlertRead,
  getOptionsDemoCampaignDetail,
  getOptionsDemoCampaigns,
  getOptionsDemoCapitalPeriods,
  getOptionsDemoCurrentCapitalPeriods,
  getOptionsDemoEquityHistory,
  getOptionsDemoExecutions,
  getOptionsDemoLegs,
  getOptionsDemoOperationDetail,
  getOptionsDemoOperations,
  getOptionsDemoOverview,
  getOptionsDemoPerformance,
  getOptionsDemoRollReviewQueue,
  rejectOptionsDemoMutation,
  toggleOptionsDemoAlertRule,
  updateOptionsDemoAlertPreferences,
  updateOptionsDemoAlertRule,
} from '../data/options/optionsDemoData';

let isOptionsDemoMode = false;

export const setOptionsDemoMode = (enabled) => {
  isOptionsDemoMode = enabled;
};

export const getOptionsDemoMode = () => isOptionsDemoMode;

const withOptionsProvider = async (demoAction, realAction) => {
  if (isOptionsDemoMode) {
    return demoAction();
  }
  return realAction();
};

export const fetchOptionsOverview = async () => {
  return withOptionsProvider(
    () => getOptionsDemoOverview(),
    async () => {
      const response = await api.get('/options/overview');
      return response.data;
    }
  );
};

export const fetchOptionsGexCurrent = async () => {
  const response = await api.get('/options/gex/current');
  return response.data;
};

export const fetchOptionsAccount = async () => {
  return withOptionsProvider(
    () => getOptionsDemoAccount(),
    async () => {
      const response = await api.get('/options/account');
      return response.data;
    }
  );
};

export const fetchOptionsEquityHistory = async ({ range = '30d', currency = null, start = null, end = null, limit = 500 } = {}) => {
  const params = new URLSearchParams();
  params.set('range', range);
  params.set('limit', String(limit));
  if (currency) params.set('currency', currency);
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  return withOptionsProvider(
    () => getOptionsDemoEquityHistory({ range, currency, start, end, limit }),
    async () => {
      const response = await api.get(`/options/equity-history?${params.toString()}`);
      return response.data;
    }
  );
};

export const fetchOptionsPerformance = async ({ range = '30d', start = null, end = null } = {}) => {
  const params = new URLSearchParams();
  params.set('range', range);
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  return withOptionsProvider(
    () => getOptionsDemoPerformance({ range, start, end }),
    async () => {
      const response = await api.get(`/options/performance?${params.toString()}`);
      return response.data;
    }
  );
};

export const fetchOptionsDashboardPerformance = async ({ range = '30d', start = null, end = null } = {}) => {
  const params = new URLSearchParams();
  params.set('range', range);
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  return withOptionsProvider(
    () => getOptionsDemoPerformance({ range, start, end }),
    async () => (await api.get(`/options/dashboard/performance?${params.toString()}`)).data
  );
};

export const fetchOptionsAnalytics = async ({ range = '30d', start = null, end = null, currency = 'usd', underlying = 'all', strategy = 'all' } = {}) => {
  const params = new URLSearchParams();
  params.set('range', range);
  params.set('currency', currency);
  params.set('underlying', underlying);
  params.set('strategy', strategy);
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  return withOptionsProvider(
    () => getOptionsDemoAnalytics({ range, start, end, currency, underlying, strategy }),
    async () => {
      const response = await api.get(`/options/analytics?${params.toString()}`);
      return response.data;
    }
  );
};

export const fetchOptionsCapitalPeriods = async ({ range = 'all', currency = null, start = null, end = null } = {}) => {
  const params = new URLSearchParams();
  params.set('range', range);
  if (currency) params.set('currency', currency);
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  return withOptionsProvider(
    () => getOptionsDemoCapitalPeriods({ range, currency, start, end }),
    async () => {
      const response = await api.get(`/options/capital-periods?${params.toString()}`);
      return response.data;
    }
  );
};

export const fetchOptionsCurrentCapitalPeriods = async ({ currency = null } = {}) => {
  const params = new URLSearchParams();
  if (currency) params.set('currency', currency);
  return withOptionsProvider(
    () => getOptionsDemoCurrentCapitalPeriods({ currency }),
    async () => {
      const response = await api.get(`/options/capital-periods/current${params.toString() ? `?${params.toString()}` : ''}`);
      return response.data;
    }
  );
};

export const fetchOptionsDashboardCapitalPeriods = async ({ range = 'all', currency = null, start = null, end = null } = {}) => {
  const params = new URLSearchParams();
  params.set('range', range);
  if (currency) params.set('currency', currency);
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  return withOptionsProvider(
    () => getOptionsDemoCapitalPeriods({ range, currency, start, end }),
    async () => (await api.get(`/options/dashboard/capital-periods?${params.toString()}`)).data
  );
};

export const fetchOptionsDashboardCurrentCapitalPeriods = async ({ currency = null } = {}) => {
  const params = new URLSearchParams();
  if (currency) params.set('currency', currency);
  return withOptionsProvider(
    () => getOptionsDemoCurrentCapitalPeriods({ currency }),
    async () => (await api.get(`/options/dashboard/current-capital-periods${params.toString() ? `?${params.toString()}` : ''}`)).data
  );
};

export const fetchOptionsPerformanceCapitalSummary = async ({ currency = null } = {}) => {
  const params = new URLSearchParams();
  if (currency) params.set('currency', currency);
  return withOptionsProvider(
    () => getOptionsDemoCapitalPeriods({ range: 'all', currency }),
    async () => (await api.get(`/options/performance/capital-summary${params.toString() ? `?${params.toString()}` : ''}`)).data
  );
};

export const fetchOptionsExecutions = async (limit = 50) => {
  return withOptionsProvider(
    () => getOptionsDemoExecutions(limit),
    async () => {
      const response = await api.get(`/options/executions?limit=${limit}`);
      return response.data;
    }
  );
};

export const fetchOptionsLegs = async (limit = 100) => {
  return withOptionsProvider(
    () => getOptionsDemoLegs(limit),
    async () => {
      const response = await api.get(`/options/legs?limit=${limit}`);
      return response.data;
    }
  );
};

export const fetchOptionsOperations = async (limit = 100) => {
  return withOptionsProvider(
    () => getOptionsDemoOperations(limit),
    async () => {
      const response = await api.get(`/options/operations?limit=${limit}`);
      return response.data;
    }
  );
};

export const fetchOptionsOperationDetail = async (operationId) => {
  return withOptionsProvider(
    () => getOptionsDemoOperationDetail(operationId),
    async () => {
      const response = await api.get(`/options/operations/${operationId}`);
      return response.data;
    }
  );
};

export const fetchOptionsCampaigns = async (limit = 100) => {
  return withOptionsProvider(
    () => getOptionsDemoCampaigns(limit),
    async () => {
      const response = await api.get(`/options/campaigns?limit=${limit}`);
      return response.data;
    }
  );
};

export const fetchOptionsCampaignDetail = async (campaignId) => {
  return withOptionsProvider(
    () => getOptionsDemoCampaignDetail(campaignId),
    async () => {
      const response = await api.get(`/options/campaigns/${campaignId}`);
      return response.data;
    }
  );
};

export const fetchOptionsPortfolioActivities = async (limit = 100) => {
  return withOptionsProvider(
    () => ({ items: [], total: 0, limit }),
    async () => {
      const response = await api.get(`/options/portfolio-activities?limit=${limit}`);
      return response.data;
    }
  );
};

export const updateOptionsPortfolioActivityRole = async (activityId, manualRole = null) => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post(`/options/portfolio-activities/${activityId}/role`, { manual_role: manualRole });
      return response.data;
    }
  );
};

export const updateOptionsManagementPositionRole = async (positionId, manualRole = null) => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post(`/options/management-positions/${positionId}/role`, { manual_role: manualRole });
      return response.data;
    }
  );
};

export const addOperationToCampaign = async (campaignId, payload) => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post(`/options/campaigns/${campaignId}/operations`, payload);
      return response.data;
    }
  );
};

export const markOperationAsNewCampaign = async (operationId) => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post(`/options/operations/${operationId}/campaign/new`);
      return response.data;
    }
  );
};

export const fetchRollReviewQueue = async (limit = 100) => {
  return withOptionsProvider(
    () => getOptionsDemoRollReviewQueue(limit),
    async () => {
      const response = await api.get(`/options/roll-review-queue?limit=${limit}`);
      return response.data;
    }
  );
};

export const confirmRollReviewCandidate = async (candidateId) => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post(`/options/roll-review-queue/${candidateId}/confirm`);
      return response.data;
    }
  );
};

export const rejectRollReviewCandidate = async (candidateId) => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post(`/options/roll-review-queue/${candidateId}/new-operation`);
      return response.data;
    }
  );
};

export const connectOptionsPreviewTestnet = async () => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post('/options/connections/testnet');
      return response.data;
    }
  );
};

export const connectOptionsManually = async (payload) => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post('/options/connections', payload);
      return response.data;
    }
  );
};

export const syncOptionsAccount = async () => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.post('/options/sync');
      return response.data;
    }
  );
};

export const disconnectOptionsAccount = async () => {
  return withOptionsProvider(
    () => rejectOptionsDemoMutation(),
    async () => {
      const response = await api.delete('/options/connections');
      return response.data;
    }
  );
};

export const fetchOptionsAlerts = async () => {
  return withOptionsProvider(
    () => getOptionsDemoAlerts(),
    async () => {
      const response = await api.get('/options/alerts');
      return response.data;
    }
  );
};

export const updateOptionsAlertsPreferences = async (payload) => {
  return withOptionsProvider(
    () => updateOptionsDemoAlertPreferences(payload),
    async () => {
      const response = await api.put('/options/alerts/preferences', payload);
      return response.data;
    }
  );
};

export const createOptionsAlertRule = async (payload) => {
  return withOptionsProvider(
    () => createOptionsDemoAlertRule(payload),
    async () => {
      const response = await api.post('/options/alerts/rules', payload);
      return response.data;
    }
  );
};

export const updateOptionsAlertRule = async (ruleId, payload) => {
  return withOptionsProvider(
    () => updateOptionsDemoAlertRule(ruleId, payload),
    async () => {
      const response = await api.put(`/options/alerts/rules/${ruleId}`, payload);
      return response.data;
    }
  );
};

export const toggleOptionsAlertRule = async (ruleId, enabled) => {
  return withOptionsProvider(
    () => toggleOptionsDemoAlertRule(ruleId, enabled),
    async () => {
      const response = await api.post(`/options/alerts/rules/${ruleId}/toggle`, { enabled });
      return response.data;
    }
  );
};

export const deleteOptionsAlertRule = async (ruleId) => {
  return withOptionsProvider(
    () => deleteOptionsDemoAlertRule(ruleId),
    async () => {
      const response = await api.delete(`/options/alerts/rules/${ruleId}`);
      return response.data;
    }
  );
};

export const markOptionsAlertRead = async (eventId) => {
  return withOptionsProvider(
    () => markOptionsDemoAlertRead(eventId),
    async () => {
      const response = await api.post(`/options/alerts/events/${eventId}/read`);
      return response.data;
    }
  );
};

export const submitOptionsFeedback = async (payload) => {
  const response = await api.post('/options/feedback', payload);
  return response.data;
};